import { useEffect } from "react";
import {
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  Link2,
  ImageIcon,
  ScanLine,
  Camera,
  FileText,
  ClipboardPaste,
  Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getDeviceHelp } from "@/lib/device";
import { logErrorEvent } from "@/lib/logError";

export type CleanError = {
  message: string;
  code?: string;
  source: "url" | "image" | "text";
  sourceLabel?: string;
};

const TITLES: Record<string, string> = {
  not_a_recipe: "We couldn't find a recipe",
  unreadable_image: "Image was hard to read",
  page_blocked: "That site blocked us",
  empty_content: "Not enough to work with",
  not_found: "Page not found",
  fetch_failed: "Couldn't reach that page",
  rate_limit: "Slow down a sec",
  no_credits: "Out of AI credits",
  ai_error: "Our AI hit a snag",
  no_response: "We couldn't read this",
  parse_error: "Something got jumbled",
  bad_input: "Missing input",
  server_error: "Server didn't respond",
  screenshot_failed: "Couldn't grab a screenshot",
  timeout: "Upload timed out",
  network_error: "Connection dropped",
  processing_failed: "Couldn't read that photo",
  upload_failed: "Couldn't upload your photo",
  unknown: "Something went wrong",
};

// Specific recovery tips by error code, overrides source-based tips when present.
const TIPS_BY_CODE: Partial<Record<string, string[]>> = {
  timeout: [
    "Your network may be slow or unstable — try again on a stronger signal",
    "If you're on cellular, switching to Wi-Fi often helps",
    "Smaller or fewer photos upload faster",
  ],
  network_error: [
    "Check your Wi-Fi or cellular connection",
    "Disable any VPN or content blocker that may be interfering",
    "Then tap Try again",
  ],
  processing_failed: [
    "Try a different photo from your library",
    "Some HEIC images from older iPhones can fail — retake or screenshot",
  ],
  rate_limit: [
    "Wait about 30 seconds, then try again",
    "If it keeps happening, the free quota may be temporarily full",
    "Saving a couple of recipes back-to-back can trip this — pace it out",
  ],
  no_credits: [
    "The shared AI quota has run out for now",
    "Upgrading unlocks reliable access without these pauses",
    "If you've already upgraded, give it a minute & try again",
  ],
  page_blocked: [
    "Open the page in your browser, select all (⌘A / Ctrl+A), copy, & paste it below — we'll filter the ads & menus",
    "Right-click → Print → Save as PDF, then upload that PDF here",
    "Some recipe sites block automated readers — paste usually works best",
  ],
  empty_content: [
    "Open the page, select all & copy, then paste it in — works even with menus, ads, & links mixed in",
    "Make sure the link goes straight to the recipe (not the homepage)",
    "If it's a video page, look for a written recipe section to paste instead",
  ],
  not_a_recipe: [
    "Open the recipe page in your browser, copy everything, & paste it below — we'll find the recipe inside",
    "Double-check the link points to an actual recipe page",
    "Category, search, & homepage URLs won't work — open the recipe itself first",
  ],
  unreadable_image: [
    "Crop tighter so just the recipe fills the frame",
    "Retake in better light, holding the camera flat above the page",
    "Screenshots are sharper than camera photos when the recipe is on a screen",
  ],
};

const TIPS_BY_SOURCE: Record<"url" | "image" | "text", string[]> = {
  url: [
    "Make sure the URL points directly to the recipe page",
    "Some sites (paywalls, recipe boxes) block automated readers",
    "Check the link opens correctly in a new tab",
  ],
  image: [
    "Use a sharp, well-lit photo with the full recipe in frame",
    "Avoid heavy glare, shadows, or angled shots",
    "Screenshots usually work best — try one if you have it",
  ],
  text: [
    "Include both the ingredient list & the cooking steps if you have them",
    "For handwritten notes, type out anything that's hard to read",
    "If you only have a list of ingredients, add a short sentence about how it's made",
  ],
};

// Codes where a screenshot/region-marking flow makes sense.
const REGION_HELP_CODES = new Set([
  "not_a_recipe",
  "page_blocked",
  "empty_content",
  "unreadable_image",
  "ai_error",
  "no_response",
  "parse_error",
]);

interface Props {
  error: CleanError;
  onRetry: () => void;
  onStartOver: () => void;
  retrying?: boolean;
  /** When provided, shows the "Mark the recipe sections" primary recovery action. */
  onMarkRegions?: () => void;
  /** When provided (URL errors), offers an "Upload screenshots / PDF" recovery path. */
  onUploadFiles?: (accept: "image" | "pdf" | "any") => void;
  /** When provided (URL errors), offers a "Paste the recipe text instead" recovery path. */
  onPasteText?: () => void;
}

export const ErrorState = ({
  error,
  onRetry,
  onStartOver,
  retrying,
  onMarkRegions,
  onUploadFiles,
  onPasteText,
}: Props) => {
  const title = TITLES[error.code || "unknown"] || TITLES.unknown;
  const tips = TIPS_BY_CODE[error.code || ""] ?? TIPS_BY_SOURCE[error.source];
  const Icon = error.source === "url" ? Link2 : error.source === "text" ? FileText : ImageIcon;
  const showMarkRegions =
    !!onMarkRegions && REGION_HELP_CODES.has(error.code || "unknown");
  const showUploadHelp = error.source === "url" && !!onUploadFiles;
  const showPasteHelp = error.source === "url" && !!onPasteText;
  const deviceHelp = showUploadHelp ? getDeviceHelp() : null;
  const isNoCredits = error.code === "no_credits";

  // Fire-and-forget telemetry so we can see which failures happen most.
  useEffect(() => {
    logErrorEvent({ code: error.code, source: error.source });
  }, [error.code, error.source]);

  const markHelper =
    error.source === "url"
      ? "We'll grab a screenshot so you can show us where the recipe is."
      : "Draw boxes around the title, ingredients, & method so we know what's what.";

  return (
    <section className="mx-auto max-w-xl px-6 pb-24 pt-10 sm:pt-16">
      <div className="animate-fade-up rounded-2xl border border-border bg-card p-8 shadow-soft sm:p-10">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
              {title}
            </h2>
            <p className="mt-2 text-base text-muted-foreground">{error.message}</p>

            {error.sourceLabel && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm text-muted-foreground">
                <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="break-all">{error.sourceLabel}</span>
              </div>
            )}
          </div>
        </div>

        {showPasteHelp && (
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
            <p className="text-sm font-medium text-foreground">
              Paste the recipe text instead — it almost always works.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Open the page in your browser, select all (⌘A on Mac, Ctrl+A on
              Windows), copy, and paste here. Don't worry about menus, ads, or
              extra text — we'll filter them out.
            </p>
            <Button
              onClick={onPasteText}
              disabled={retrying}
              className="mt-3 h-11 w-full"
            >
              <ClipboardPaste className="h-4 w-4" />
              Paste the recipe text
            </Button>
          </div>
        )}

        {showUploadHelp && (
          <div className="mt-6 rounded-xl border border-border bg-muted/40 p-4">
            <p className="text-sm font-medium text-foreground">
              {showPasteHelp
                ? "Or upload screenshots / a PDF of the page."
                : "Try uploading screenshots or a PDF version of the page."}
            </p>

            {deviceHelp && !showPasteHelp && (
              <div className="mt-3 rounded-lg bg-background/60 p-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {deviceHelp.label}
                </p>
                <ul className="mt-1.5 space-y-1 text-sm text-foreground/80">
                  {deviceHelp.instructions.map((step) => (
                    <li key={step} className="flex gap-2">
                      <span aria-hidden className="text-muted-foreground">•</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Button
                variant={showPasteHelp ? "outline" : "default"}
                onClick={() => onUploadFiles?.("image")}
                className="h-11 sm:flex-1"
              >
                <Camera className="h-4 w-4" />
                Upload screenshots
              </Button>
              <Button
                variant="outline"
                onClick={() => onUploadFiles?.("pdf")}
                className="h-11 sm:flex-1"
              >
                <FileText className="h-4 w-4" />
                Upload PDF
              </Button>
            </div>
          </div>
        )}

        {showMarkRegions && (
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
            <Button
              onClick={onMarkRegions}
              disabled={retrying}
              className="h-11 w-full"
              variant={showUploadHelp ? "outline" : "default"}
            >
              <ScanLine className="h-4 w-4" />
              Mark the recipe sections
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">{markHelper}</p>
          </div>
        )}

        <div className="mt-6 rounded-xl bg-muted/50 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            What to try
          </p>
          <ul className="space-y-1.5 text-sm text-foreground/80">
            {tips.map((tip) => (
              <li key={tip} className="flex gap-2">
                <span aria-hidden className="text-muted-foreground">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {isNoCredits ? (
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Button asChild className="h-11 sm:flex-1">
              <a href="mailto:hello@cleanplate.app?subject=Out%20of%20AI%20credits">
                <Crown className="h-4 w-4" />
                Get in touch about upgrading
              </a>
            </Button>
            <Button variant="ghost" onClick={onStartOver} className="h-11 sm:flex-1">
              <ArrowLeft className="h-4 w-4" />
              Start over
            </Button>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <Button
              variant={showMarkRegions || showUploadHelp || showPasteHelp ? "outline" : "default"}
              onClick={onRetry}
              disabled={retrying}
              className="h-11 sm:flex-1"
            >
              <RefreshCw className={`h-4 w-4 ${retrying ? "animate-spin" : ""}`} />
              {retrying ? "Trying again…" : "Try again"}
            </Button>
            <Button variant="ghost" onClick={onStartOver} className="h-11 sm:flex-1">
              <ArrowLeft className="h-4 w-4" />
              Start over
            </Button>
          </div>
        )}

      </div>
    </section>
  );
};
